import EventAnalytics from "~/js/utilities/EventAnalytics";
import { calculatePercentage } from "~/solidJs/shared";
import type { TeamMemberCredits } from "~/solidJs/shared/api/CreditsApi";
import type {
	MemberRole,
	NextPayment,
	TeamInfoResponse,
	TeamMember
} from "~/solidJs/shared/api/TeamApi";
import type { IUser } from "~/solidJs/shared/helpers/types";
import { getUserStore } from "../../HeaderMain/models";

// Common interface for member credits (works with both TeamMember and TeamMemberCredits)
type MemberWithCredits = Pick<TeamMember, "credits_balance" | "credits_break_down">;

export * from "./teamSeatsModal";
export * from "./teamUnsubscribeModal";
export * from "./updateSeats";
export * from "./updateSeatsModal";

export const SEATS_LIMITS = {
	DISCOUNT_10_MAX: 4,
	DISCOUNT_10: 5,
	DISCOUNT_20: 10,
	CONTACT_SALES_MIN: 11
} as const;

/**
 * Function to generate promo message text
 */
export const getPromoMessage = (seats: number): string | null => {
	if (seats >= 1 && seats < SEATS_LIMITS.DISCOUNT_10) {
		return `Add ${SEATS_LIMITS.DISCOUNT_10 - seats} more seats to get 10% off annually`;
	}
	if (seats >= SEATS_LIMITS.DISCOUNT_10 && seats < SEATS_LIMITS.DISCOUNT_20) {
		return `Add ${SEATS_LIMITS.DISCOUNT_20 - seats} more seats to get 20% off annually`;
	}
	if (seats === SEATS_LIMITS.DISCOUNT_20) {
		return "You get 20% discount annualy";
	}
	return null;
};

/**
 * Function to check if "Contact Sales" should be shown
 */
export const shouldShowContactSales = (seats: number, triggerEvent = false): boolean => {
	const shouldShow = seats >= SEATS_LIMITS.CONTACT_SALES_MIN;
	if (shouldShow && triggerEvent) {
		EventAnalytics.team.createTeam.seatsLimitReached(seats);
	}
	return shouldShow;
};

/**
 * Get team name
 */
export const getTeamName = (team: TeamInfoResponse | null): string => {
	if (!team) return "";
	return team.team_name;
};

/**
 * Get team plan name
 */
export const getUserTeamPlanName = (team: TeamInfoResponse | null, _user: IUser | null): string => {
	if (!team) return "";

	// Always use tariff_name from /team/info
	return team.tariff_name || "";
};

/**
 * Get team member count
 */
export const getTeamMemberAmount = (team: TeamInfoResponse | null): number => {
	if (!team) return 0;
	return team.team_member_amount;
};

/**
 * Get total team member count
 */
export const getTeamMemberTotalAmount = (team: TeamInfoResponse | null): number => {
	if (!team) return 0;
	return team.team_member_total_amount;
};

/**
 * Check if current user is team owner
 */
export const isUserTeamOwner = (team: TeamInfoResponse | null): boolean => {
	if (!team) return false;
	return team.your_role === "owner";
};

/**
 * should we render blank seat ui, the user for this seat is not presented currently

 */
export const isBlankMember = (user: TeamMember): boolean => {
	if (!user.user_id && !user.invitation_token) return true;
	if (!user.user_id && !user.user_name) return true;
	return false;
};

/**
 * Get team members list
 */
export const getTeamMembers = (team: TeamInfoResponse | null): TeamMember[] => {
	if (!team || !team.members) return [];
	return team.members;
};

/**
 * Get current user's role in team
 */
export const getUserTeamRole = (team: TeamInfoResponse | null): MemberRole | null => {
	if (!team) return null;
	return team.your_role;
};

/**
 * Get company status
 */
export const getCompanyStatus = (team: TeamInfoResponse | null): "active" | "inactive" | null => {
	if (!team) return null;
	return team.company_status;
};

/**
 * Get team subscription status
 * inactive - not paid
 * active - paid and active
 * cancelled - only for owner when recurrent disabled
 * due - ended but within 10 days grace period
 */
export const getTeamSubscriptionStatus = (
	team: TeamInfoResponse | null
): "inactive" | "active" | "cancelled" | "due" => {
	if (!team) return "inactive";
	return team.team_subscription_status;
};

/**
 * Check if subscription is in grace period (due status)
 * This means subscription ended but user has 10 days to renew
 */
export const isSubscriptionDue = (team: TeamInfoResponse | null): boolean => {
	if (!team) return false;
	return team.team_subscription_status === "due";
};

/**
 * Check if subscription is expired (inactive and not in grace period)
 */
export const isSubscriptionExpired = (team: TeamInfoResponse | null): boolean => {
	if (!team) return false;
	return team.team_subscription_status === "inactive";
};

/**
 * Get total credits balance of all team members
 */
export const getTeamCreditsBalance = (team: TeamInfoResponse | null): number => {
	if (!team || !team.members) return 0;

	return team.members.reduce((total, member) => {
		const memberBalance = getMemberCurrentCreditsBalance(member);
		return total + memberBalance;
	}, 0);
};

/**
 * Get total credits issued to all team members
 */
export const getTotalTeamCreditsReceived = (team: TeamInfoResponse | null): number => {
	if (!team || !team.members) return 0;

	return team.members.reduce((total, member) => {
		const memberReceived = getMemberCreditsTotal(member);
		return total + memberReceived;
	}, 0);
};

export const getMemberValue = <Out>(
	team: TeamInfoResponse | null,
	user: IUser | null,
	fn: (member: TeamMember) => Out
): Out | null => {
	if (!team || !team.members || !user) return null;

	const member = team.members.find((m) => m.user_id === user.id);
	if (!member) return null;

	return fn(member);
};

/**
 * Get current credits balance of specific member
 * Works with both TeamMember and TeamMemberCredits
 */
export const getMemberCurrentCreditsBalance = (
	member: MemberWithCredits | TeamMemberCredits
): number => {
	return member.credits_balance;
};

/**
 * Get total credits issued to specific member
 * Works with both TeamMember and TeamMemberCredits
 */
export const getMemberCreditsTotal = (member: MemberWithCredits | TeamMemberCredits): number => {
	return member.credits_break_down.reduce(
		(sum: number, breakdown: { credits_received: number }) => sum + breakdown.credits_received,
		0
	);
};

/**
 * Check if member accepted invitation
 */
export const isMemberAccepted = (member: TeamMember): boolean => {
	return member.invitation_status === "accepted";
};

/**
 * Check if member invitation is pending
 */
export const isMemberPending = (member: TeamMember): boolean => {
	return member.invitation_status === "pending";
};

/**
 * Get team member role
 */
export const getTeamMemberRole = (member: TeamMember): MemberRole | null => {
	return member.role;
};

export const getMemberId = (member: TeamMember): string | null => {
	return member.user_id;
};

/**
 * get credits usage percentage for logd in user
 */
export const getCurrentUserCreditsUsagePercentage = (team: TeamInfoResponse | null): number => {
	const currentUserId = getUserStore()?.id;
	if (!currentUserId) return 0;
	const currentUserMemberEntrie = team?.members?.find(
		(member) => member.user_id === currentUserId
	);
	if (!currentUserMemberEntrie) return 0;
	return Math.min(
		100,
		calculatePercentage(
			0,
			getMemberCreditsTotal(currentUserMemberEntrie),
			getMemberCurrentCreditsBalance(currentUserMemberEntrie)
		)
	);
};

/**
 * Get percentage of credits used by member
 */
export const getMemberCreditsUsagePercentage = (member: TeamMember): number => {
	return Math.min(
		100,
		calculatePercentage(
			0,
			getMemberCreditsTotal(member),
			getMemberCurrentCreditsBalance(member)
		)
	);
};

/**
 * Get percentage of credits used by team
 */
export const getTeamCreditsUsagePercentage = (team: TeamInfoResponse | null): number => {
	return Math.min(
		100,
		calculatePercentage(0, getTotalTeamCreditsReceived(team), getTeamCreditsBalance(team))
	);
};

/**
 * Get accepted invitations count
 */
export const getAcceptedMembersCount = (team: TeamInfoResponse | null): number => {
	if (!team || !team.members) return 0;
	return team.members.filter((member) => isMemberAccepted(member)).length;
};

/**
 * Get pending invitations count
 */
export const getPendingMembersCount = (team: TeamInfoResponse | null): number => {
	if (!team || !team.members) return 0;
	return team.members.filter((member) => isMemberPending(member)).length;
};

/**
 * Get next payment info
 */
export const getNextPayment = (team: TeamInfoResponse | null): NextPayment | null => {
	if (!team) return null;
	return team.next_payment || null;
};

/**
 * Get subscription end time (when subscription cancels)
 */
export const getSubscriptionEndTime = (team: TeamInfoResponse | null): string | null => {
	if (!team) return null;
	return team.end_time || null;
};

/**
 * Get current user's expiration date from team (credits refresh date)
 */
export const getCurrentUserExpirationDate = (team: TeamInfoResponse | null): string | null => {
	const currentUserId = getUserStore()?.id;
	if (!currentUserId || !team?.members) return null;

	const currentUserMember = team.members.find((member) => member.user_id === currentUserId);
	return currentUserMember?.expiration_date || null;
};

/**
 * Check if subscription is cancelled
 * Uses team_subscription field: 'cancelled' means owner disabled recurrent payment
 */
export const isSubscriptionCancelled = (team: TeamInfoResponse | null): boolean => {
	if (!team) return false;
	return team.team_subscription_status === "cancelled";
};

/**
 * Get subscription start time
 */
export const getSubscriptionStartTime = (team: TeamInfoResponse | null): string | null => {
	if (!team) return null;
	return team.start_time || null;
};
